"use client";

import { createContext, type ReactNode, useContext } from "react";

import { plainAdapter } from "@/ui/app-link/plain";
import type {
  AppImageProps,
  AppLinkAdapter,
  AppLinkProps,
  AppRouter,
  AppSearchParams,
} from "@/ui/app-link/types";

const AdapterContext = createContext<AppLinkAdapter>(plainAdapter);

interface AppLinkProviderProps {
  adapter: AppLinkAdapter;
  children: ReactNode;
}

export function AppLinkProvider(props: AppLinkProviderProps) {
  return (
    <AdapterContext.Provider value={props.adapter}>
      {props.children}
    </AdapterContext.Provider>
  );
}

/** A link through the mounted framework; a plain `<a>` without one. */
export function AppLink(props: AppLinkProps) {
  const { Link } = useContext(AdapterContext);
  return <Link {...props} />;
}

/** An image through the mounted framework; a plain `<img>` without one. */
export function AppImage(props: AppImageProps) {
  const { Image } = useContext(AdapterContext);
  return <Image {...props} />;
}

/**
 * The adapter is fixed for a provider's lifetime, so each hook below
 * calls the same framework hook on every render.
 */
export function useAppRouter(): AppRouter {
  return useContext(AdapterContext).useRouter();
}

export function useAppPathname(): string {
  return useContext(AdapterContext).usePathname();
}

export function useAppSearchParams(): AppSearchParams {
  return useContext(AdapterContext).useSearchParams();
}

/** True while the enclosing `AppLink`'s navigation is pending. */
export function useAppLinkPending(): boolean {
  return useContext(AdapterContext).useLinkPending();
}
